import { useEffect, useRef, useState } from 'react'

import { getAIConversationPendingAnswers } from '../../../../api'
import { getAIConversationPendingAnswerFileName } from '../../../../utils/aiAgents'

export function useGretaAIChatPendingAnswerLabel({
  activeChatId,
  getIsOpenConversation,
}) {
  const pendingAnswerFileNamesRef = useRef(new Map())

  // Bumped per wait so a stale request can't land after a newer one settled.
  const pendingAnswerEpochsRef = useRef(new Map())

  const [pendingAnswerFileName, setPendingAnswerFileName] = useState(null)

  // Re-syncs on chat switch so the previous chat's label doesn't show here.
  useEffect(() => {
    setPendingAnswerFileName(
      pendingAnswerFileNamesRef.current.get(activeChatId) ?? null
    )
  }, [activeChatId])

  function bumpPendingAnswerEpoch(conversationId) {
    const next = (pendingAnswerEpochsRef.current.get(conversationId) ?? 0) + 1

    pendingAnswerEpochsRef.current.set(conversationId, next)

    return next
  }

  // `pendingAnswers`: a response the caller already has, instead of asking again.
  async function refreshPendingAnswerLabel(conversationId, pendingAnswers) {
    const epoch = bumpPendingAnswerEpoch(conversationId)

    const fileName = await Promise.resolve(
      pendingAnswers ?? getAIConversationPendingAnswers({ id: conversationId })
    )
      .then(getAIConversationPendingAnswerFileName)
      .catch(() => null)

    // Discarded if a newer wait for this chat started while this was in flight.
    if (pendingAnswerEpochsRef.current.get(conversationId) === epoch) {
      pendingAnswerFileNamesRef.current.set(conversationId, fileName)

      if (getIsOpenConversation(conversationId)) {
        setPendingAnswerFileName(fileName)
      }
    }
  }

  function clearPendingAnswerLabel(conversationId) {
    bumpPendingAnswerEpoch(conversationId)
    pendingAnswerFileNamesRef.current.delete(conversationId)

    if (getIsOpenConversation(conversationId)) {
      setPendingAnswerFileName(null)
    }
  }

  return {
    clearPendingAnswerLabel,
    pendingAnswerFileName,
    refreshPendingAnswerLabel,
  }
}
